import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Shared dialog behaviour: Escape, a Tab/Shift+Tab focus trap, focus moved
 * into the dialog when it opens and handed back when it closes, and a page
 * scroll lock that nested dialogs release in the right order.
 *
 * Ported from Engage's `components/ui/dialog-a11y.ts` so the two apps' dialogs
 * behave alike. Before this, Flow's Modal bound Escape at the document once
 * per open modal — so a confirm opened on top of a form closed BOTH — never
 * moved focus, and let Tab walk out of the dialog into the page behind it.
 */

/**
 * Tab-reachable elements. `[tabindex="-1"]` is excluded on purpose — that is
 * what the dialog container itself carries so it can receive focus
 * programmatically without ever appearing in the cycle.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  "input:not([disabled]):not([type='hidden'])",
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  "[contenteditable]:not([contenteditable='false'])",
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isFocusable(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if (el.closest('[inert]')) return false;
  const style = el.ownerDocument.defaultView?.getComputedStyle(el);
  if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
  return true;
}

/** Focusable descendants of `root`, in tab order (document order). */
export function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusable);
}

/**
 * Every currently-open dialog, oldest first. Only the last entry reacts to
 * Escape or Tab, so a dialog stacked on another does not fight it.
 */
const openDialogs: object[] = [];

export interface DialogA11yOptions {
  open: boolean;
  onClose: () => void;
  /** The element carrying `role="dialog"`. Focus is trapped inside it. */
  containerRef: RefObject<HTMLElement | null>;
  /** Whether Escape closes the dialog. It still only ever reaches the topmost. */
  closeOnEscape?: boolean;
}

export function useDialogA11y({
  open,
  onClose,
  containerRef,
  closeOnEscape = true,
}: DialogA11yOptions): void {
  // Callers pass an inline arrow for onClose, so its identity changes on every
  // parent render. Reading it through a ref keeps the effect below depending
  // on `open` alone — otherwise each re-render would pop this dialog off the
  // stack and push it back on top, letting a BACKGROUND dialog steal Escape
  // from the one actually on screen.
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscapeRef.current = closeOnEscape;
  });

  // What to hand focus back to when the dialog closes, read while RENDERING
  // it open — before the commit in which a field inside it takes focus with
  // `autoFocus`. Read in the focus effect below, it was already that field,
  // which is gone by the time the dialog closes, so focus fell to the top of
  // the page instead of returning to the button that opened the dialog.
  const [opener, setOpener] = useState<HTMLElement | null>(() => (open ? focusedElement() : null));
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpener(focusedElement());
  }

  // Escape + focus trap, gated on being the topmost open dialog.
  useEffect(() => {
    if (!open) return;
    const entry = {};
    openDialogs.push(entry);

    const isTopmost = () => openDialogs[openDialogs.length - 1] === entry;

    function handleKeyDown(e: KeyboardEvent) {
      if (!isTopmost()) return;

      if (e.key === 'Escape') {
        // Something inside already answered it — a menu or a picker closing
        // itself. Closing the dialog as well would be one Escape doing two jobs.
        if (e.defaultPrevented) return;
        if (closeOnEscapeRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const root = containerRef.current;
      if (!root) return;
      const active = root.ownerDocument.activeElement as HTMLElement | null;

      // Focus sitting outside the dialog but not on <body> means something
      // portalled (a menu, a popover) took it and is logically part of this
      // dialog. Cycling would yank the user out of it, so leave Tab alone.
      const outside = active !== null && !root.contains(active);
      if (outside && active !== root.ownerDocument.body) return;

      const focusables = getFocusableElements(root);
      if (focusables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (active === null || outside || active === root) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const i = openDialogs.indexOf(entry);
      if (i !== -1) openDialogs.splice(i, 1);
    };
  }, [open, containerRef]);

  // Page scroll lock. Nested dialogs restore in LIFO order, so the innermost
  // hands back "hidden" and the outermost hands back the page's real value.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Focus in on open, back out on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = opener ?? focusedElement();
    // Captured once, not read from the ref in the cleanup: cleanups run after
    // React has detached the node and nulled the ref.
    const root = containerRef.current;

    // Deferred a tick: a field with `autoFocus` claims focus during mount, and
    // it should keep it.
    const timer = setTimeout(() => {
      if (!root || root.contains(document.activeElement)) return;
      // The dialog itself, not its first field: the first Tab then lands on
      // the first control, a screen reader announces the dialog by name, and
      // nothing the user has not chosen starts accepting keystrokes.
      root.focus({ preventScroll: true });
    }, 0);

    return () => {
      clearTimeout(timer);
      const active = document.activeElement as HTMLElement | null;
      // If focus is somewhere else entirely, something took it on purpose (a
      // second dialog, a toast action); stealing it back would be worse than
      // not restoring.
      const focusIsOurs =
        active === null || active === document.body || (root !== null && root.contains(active));
      if (!focusIsOurs) return;
      if (
        previouslyFocused &&
        previouslyFocused !== document.body &&
        previouslyFocused.isConnected &&
        typeof previouslyFocused.focus === 'function'
      ) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open, containerRef, opener]);
}

function focusedElement(): HTMLElement | null {
  return typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null);
}
